<template>
  <div class="skills-panel">
    <div v-if="skillToasts.length" class="skill-toasts">
      <div v-for="toast in skillToasts" :key="toast.id" class="skill-toast">
        <span class="toast-icon">&#10003;</span>
        Skill learned: <strong>{{ toast.displayDescription }}</strong>
      </div>
    </div>

    <!-- ACTIVE section -->
    <div v-if="activeItems.length" class="skills-section">
      <h3 class="section-title">Active</h3>
      <div v-for="skill in activeItems" :key="'active-' + skill.name" class="skill-item active" @click="deactivateSkill(skill)">
        <span class="skill-check">&#10003;</span>
        <div class="skill-info">
          <span class="skill-name">{{ skill.name }}</span>
          <span v-if="skill.displayDescription" class="skill-description">{{ skill.displayDescription }}</span>
        </div>
      </div>
    </div>

    <!-- AVAILABLE section -->
    <div v-if="availableItems.length" class="skills-section">
      <h3 class="section-title">Available</h3>
      <div v-for="skill in availableItems" :key="'available-' + skill.name" class="skill-item" @click="activateSkill(skill)">
        <span class="skill-check empty">&#10003;</span>
        <div class="skill-info">
          <span class="skill-name">{{ skill.name }}</span>
          <span v-if="skill.displayDescription" class="skill-description">{{ skill.displayDescription }}</span>
        </div>
      </div>
    </div>

    <div v-if="!activeItems.length && !availableItems.length" class="empty">
      <p>No skills configured for this chat.</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    activeSkills: {
      type: Array,
      default: () => []
    },
    allowedSkills: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentActiveNames: [...(this.activeSkills || [])]
    };
  },
  created() {
    const learned = this.$store?.state?.prompts?.sample?.learnedSkills ?? [];
    const allowed = new Set([...(this.activeSkills || []), ...(this.allowedSkills || [])]);
    let changed = false;
    for (const name of learned) {
      if (!this.currentActiveNames.includes(name) && allowed.has(name)) {
        this.currentActiveNames.push(name);
        changed = true;
      }
    }
    if (changed) {
      this.emitActiveSkills();
    }
  },
  computed: {
    ...mapState({
      skills: s => s.nlp.skills,
      skillToasts: s => s.prompts.sample.skillToasts,
      learnedSkills: s => s.prompts.sample.learnedSkills
    }),
    allKnownNames() {
      return [...new Set([...(this.activeSkills || []), ...(this.allowedSkills || [])])];
    },
    activeItems() {
      return this.currentActiveNames.map(name => {
        const skill = this.skills.find(s => s.name === name);
        return skill || { name, displayDescription: '' };
      });
    },
    availableItems() {
      const activeSet = new Set(this.currentActiveNames);
      return this.allKnownNames
        .filter(name => !activeSet.has(name))
        .map(name => {
          const skill = this.skills.find(s => s.name === name);
          return skill || { name, displayDescription: '' };
        });
    }
  },
  watch: {
    learnedSkills: {
      handler(newVal) {
        if (!newVal) return;
        for (const name of newVal) {
          if (!this.currentActiveNames.includes(name) && this.allKnownNames.includes(name)) {
            this.currentActiveNames.push(name);
          }
        }
        this.emitActiveSkills();
      },
      deep: true
    }
  },
  methods: {
    activateSkill(skill) {
      if (this.currentActiveNames.includes(skill.name)) return;
      this.currentActiveNames.push(skill.name);
      this.emitActiveSkills();
    },
    deactivateSkill(skill) {
      const idx = this.currentActiveNames.indexOf(skill.name);
      if (idx === -1) return;
      this.currentActiveNames.splice(idx, 1);
      if (this.learnedSkills && this.learnedSkills.includes(skill.name)) {
        this.$store.commit('prompts/sample/REMOVE_LEARNED_SKILL', skill.name);
      }
      this.emitActiveSkills();
    },
    emitActiveSkills() {
      this.$emit('update:active-skills', [...this.currentActiveNames]);
    }
  }
};
</script>

<style lang="scss" scoped>
.skills-panel {
  height: 100%;
  overflow-y: auto;
  padding: 0.75rem;

  .skill-toasts {
    margin-bottom: 0.75rem;

    .skill-toast {
      background: var(--theme-primary);
      color: #fff;
      padding: 0.4rem 0.75rem;
      border-radius: 4px;
      margin-bottom: 0.25rem;
      font-size: 0.8rem;
      display: flex;
      align-items: center;
      gap: 0.4rem;

      .toast-icon {
        font-weight: bold;
      }
    }
  }

  .skills-section {
    margin-bottom: 1rem;

    .section-title {
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--theme-on-surface);
      opacity: 0.6;
      margin: 0 0 0.5rem 0;
    }

    .skill-item {
      display: flex;
      align-items: flex-start;
      padding: 0.5rem;
      border-radius: 4px;
      margin-bottom: 0.25rem;
      background: var(--theme-background);
      cursor: pointer;
      min-height: 30px;

      &:hover {
        background: var(--theme-on-background-dark);
      }

      .skill-check {
        width: 20px;
        flex-shrink: 0;
        font-size: 0.75rem;
        font-weight: bold;
        color: var(--theme-primary);
        text-align: center;
        margin-top: 0.1rem;

        &.empty {
          color: transparent;
        }
      }

      .skill-info {
        display: flex;
        flex-direction: column;
        margin-left: 0.25rem;
        overflow: hidden;

        .skill-name {
          font-size: 0.85rem;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .skill-description {
          font-size: 0.75rem;
          color: var(--theme-on-surface);
          opacity: 0.7;
          margin-top: 0.1rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .empty {
    padding: 1rem;
    text-align: center;
    color: var(--theme-on-surface);
    opacity: 0.6;
    font-style: italic;
    font-size: 0.85rem;
  }
}
</style>
